import { useEffect, useRef, useState } from "react";
import { MoreHorizontal } from "lucide-react";

interface OverflowMenuProps {
  /**
   * Menu contents. May be a render function that receives a `close` callback so
   * items can dismiss the menu when activated.
   */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  ariaLabel?: string;
  align?: "left" | "right";
  /** Override the trigger button classes (e.g. for contrast on a colored header). */
  triggerClassName?: string;
  /** Custom trigger content (defaults to an ellipsis icon). */
  trigger?: React.ReactNode;
}

const DEFAULT_TRIGGER =
  "rounded p-1.5 leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700";

/**
 * An ellipsis button that toggles a small dropdown menu. Closes on outside
 * click, and on Escape while focus is within the menu (the keydown is scoped to
 * the menu subtree, so a surrounding Modal's Escape stack is left untouched
 * unless the menu itself is focused).
 */
export function OverflowMenu({
  children,
  ariaLabel = "More actions",
  align = "right",
  triggerClassName = DEFAULT_TRIGGER,
  trigger,
}: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div
      ref={ref}
      className="relative"
      onKeyDown={(e) => {
        if (open && e.key === "Escape") {
          e.stopPropagation();
          close();
        }
      }}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName}
      >
        {trigger ?? <MoreHorizontal size={16} />}
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute z-30 mt-1 min-w-40 rounded-lg border border-slate-200 bg-[var(--surface)] py-1 shadow-lg ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {typeof children === "function" ? children(close) : children}
        </div>
      )}
    </div>
  );
}
